"use client";

import { collection, doc, getDocs, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

/**
 * KYC documents live in Firestore rather than Cloud Storage: the project runs on the Spark
 * plan, which cannot provision a storage bucket. Photos are downscaled in the browser so a
 * document stays well inside the 1 MiB Firestore limit.
 */
export const kycUploadsConfigured = true;

export type KycDocPurpose = "id_proof" | "selfie" | "workshop";

export type KycDocument = {
  id: string;
  purpose: KycDocPurpose;
  contentType: string;
  dataUrl: string;
};

const MAX_STORED_CHARS = 700_000;
const MAX_EDGE_PX = 1400;
const QUALITY_STEPS = [0.82, 0.7, 0.58, 0.45, 0.34, 0.25];

function contentTypeFor(file: File) {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["jpg", "jpeg", "jfif"].includes(ext)) return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  if (ext === "pdf") return "application/pdf";
  return "";
}

function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read that file — try attaching it again"));
    reader.readAsDataURL(file);
  });
}

async function compressToJpeg(file: File) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not process this photo — try a different one");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  for (const quality of QUALITY_STEPS) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length <= MAX_STORED_CHARS) return dataUrl;
  }
  throw new Error("Photo is too large to store — retake it at a lower resolution");
}

/** Returns the stored document id, which the application record keeps as its doc path. */
export async function uploadPartnerKycDocument(file: File, purpose: KycDocPurpose = "id_proof") {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error("Create and sign in to your account before uploading KYC documents");
  }
  const sourceType = contentTypeFor(file);
  const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]);
  if (!allowedTypes.has(sourceType)) throw new Error("Upload PDF, JPG, PNG or WebP only");
  if (file.size <= 0 || file.size > 8 * 1024 * 1024) throw new Error("KYC document must be under 8 MB");

  const isPdf = sourceType === "application/pdf";
  const dataUrl = isPdf ? await readAsDataUrl(file) : await compressToJpeg(file);
  if (dataUrl.length > MAX_STORED_CHARS) {
    throw new Error("This PDF is too large — upload a clear photo of the document instead");
  }

  const docId = `${purpose}-${crypto.randomUUID()}`;
  await setDoc(doc(db, "partnerApplications", user.uid, "kycDocs", docId), {
    purpose,
    contentType: isPdf ? "application/pdf" : "image/jpeg",
    dataUrl,
    bytes: dataUrl.length,
    uploadedBy: user.uid,
    createdAt: serverTimestamp(),
  });
  return docId;
}

/** Admin review: pulls the stored KYC documents for one applicant. */
export async function loadPartnerKycDocuments(partnerId: string) {
  const snapshot = await getDocs(
    query(collection(db, "partnerApplications", partnerId, "kycDocs"), orderBy("createdAt", "asc")),
  );
  return snapshot.docs.map((entry) => {
    const data = entry.data();
    return {
      id: entry.id,
      purpose: String(data.purpose || "id_proof") as KycDocPurpose,
      contentType: String(data.contentType || "image/jpeg"),
      dataUrl: String(data.dataUrl || ""),
    } satisfies KycDocument;
  });
}
